import { useState } from 'react';
import { faChevronDown } from '@fortawesome/pro-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-solid-svg-icons/faChevronUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Anchor, Collapse, Text, UnstyledButton } from '@mantine/core';
import { useIntl } from 'react-intl';

import { wizardCostBreakdownMessages } from './wizard-cost-breakdown-messages';
import styles from './WizardCostBreakdown.module.css';

import type { LtlQuote, LtlQuoteCharge } from '../../../../types/ltl';

type WizardCostBreakdownProps = {
  selectedRate: LtlQuote | null;
};

const CHARGE_CODE_LABEL: Record<string, keyof typeof wizardCostBreakdownMessages> = {
  // Freight
  FREIGHT: 'chargeFreight',
  FREIGHT_MIN: 'chargeFreightMinimum',
  FRT: 'chargeFreight',
  LHS: 'chargeLineHaulSurcharge',

  // Fuel
  FSC: 'chargeFuelSurcharge',
  FSCNET: 'chargeFuelSurcharge',
  FUEL: 'chargeFuelSurcharge',

  // Insurance / Declared Value
  INS: 'chargeInsurance',
  INSURANCE: 'chargeInsurance',
  DVS: 'chargeDeclaredValueSurcharge',
  EVS: 'chargeExtendedValueSurcharge',

  // Hazardous material
  HAZM: 'hazardousMaterialValueSurcharge',

  // Handling & Fees
  CHC: 'chargeCustomerHandlingCharge',
  CARBFEE: 'chargeCarbComplianceFee',
  CARDFEE: 'chargeCreditCardFee',
  INVPRCFEE: 'chargeInvoiceProcessing',
  NSFFEE: 'chargeNonSufficientFunds',
  PAPERFEE: 'chargePaperInvoiceFee',
  SHFFEE: 'chargeShippingHandlingFee',
  MARKETING_FUND_FEE: 'chargeMarketingFund',
  USER_FEE: 'chargeTechHosting',
  TERRITORY_FEE: 'chargeTerritoryFee',

  // Accessorials
  APPT: 'chargeAppointmentRequired',
  COD: 'chargeCollectOnDelivery',
  GUR: 'chargeGuaranteedDelivery',
  GRDEL: 'chargeGuaranteedDelivery',
  SAT: 'chargeSaturdayDelivery',
  SIGDEL: 'chargeSignatureRequired',
  INPU: 'chargeInsidePickup',
  INDEL: 'chargeInsideDelivery',
  OVDIM: 'chargeOversize',
  AHC: 'chargeAdditionalHandlingDimensional',
  AHW: 'chargeAdditionalHandlingWeight',
  TCKNU: 'chargeTrackingNumber',
  COO: 'chargeCertificateOfOrigin',
  '36': 'chargeElectronicExportInfo',

  // Residential
  HRS: 'chargeResidentialSurcharge',
  RES: 'chargeResidentialSurcharge',
  REP: 'chargeResidentialSurcharge',
  RESDEL: 'chargeResidentialDelivery',
  PFR: 'chargeSurgeFee',
  PSR: 'chargeResidentialSurcharge',

  // Liftgate
  LCC: 'chargeLiftgate',
  LGDEL: 'chargeLiftgateDelivery',
  LGPU: 'chargeLiftgatePickup',
  LGP: 'chargeLiftgatePickup',
  LGBU: 'chargeLiftgateBulk',

  // High Cost / Remote Area
  HCP: 'chargeHighCostPickup',
  HCD: 'chargeHighCostDelivery',
  IHCP: 'chargeIntlHighCostPickup',
  IHCD: 'chargeIntlHighCostDelivery',
  HCPA: 'chargeHighCostPickup',
  HCDA: 'chargeHighCostDelivery',
  HCFP: 'chargeHighCostPickup',
  HCFD: 'chargeHighCostDelivery',
  HBR: 'chargeRemoteArea',
  OVR: 'chargeOverLimitSurcharge',
  EXCESSMILES: 'chargeExcessMiles',

  // Metro / Geographic
  METRO: 'chargeCongestedMetro',
  FERRY: 'chargeFerrySurcharge',
  PFC: 'chargeSurgeFee',
  METROPU: 'chargeMetroPickup',

  // Limited Access
  LTDACC: 'chargeLimitedAccess',
  LTDDEL: 'chargeLimitedAccessDelivery',
  LTDPU: 'chargeLimitedAccessPickup',

  // Miscellaneous
  MIS: 'chargeMiscellaneous',
  MSC: 'chargeMiscSurcharge',
  ASC: 'chargeAccessorialSurcharge',
  ADS: 'chargeAdditionalSurcharge',
  ADD: 'chargeAdditionalCharge',
  CSV: 'chargeCarrierSurcharge',
  DCR: 'chargeDeliverySurcharge',
  NOTIFY: 'chargeAdvanceNotification',
  HOLD: 'chargeHoldForPickup',
  SINGSHIP: 'chargeSingleShipment',
  SORT: 'chargeSortSegregate',
  CARBON: 'chargeCarbonSurcharge',
  CAL_CLEAN_AIR: 'chargeCalCleanAir',
  BORDER_PROCESSING: 'chargeBorderProcessing',
  DOCFEE: 'chargeDocumentFee',
  MIG_REV_SHARE: 'chargeMigRevShare',

  // Franchise / Royalty
  FRANCHISE_ROYALTY_TOTAL_EXP_MARGIN: 'chargeRoyaltyExpressTotalMargin',
  FRANCHISE_ROYALTY_TOTAL_EXP_SALE_PRICE: 'chargeRoyaltyExpressSalePrice',
  FRANCHISE_ROYALTY_EXPRESS_CHARGE: 'chargeRoyaltyExpressCharge',
  FRANCHISE_ROYALTY_TOTAL_FREIGHT_MARGIN: 'chargeRoyaltyFreightTotalMargin',
  FRANCHISE_ROYALTY_TOTAL_FREIGHT_SALE_PRICE: 'chargeRoyaltyFreightSalePrice',
  FRANCHISE_ROYALTY_FREIGHT_CHARGE: 'chargeRoyaltyFreightCharge',
  FRANCHISE_MINIMUM_ROYALTY_CHARGE: 'chargeRoyaltyMinimum',
  FRANCHISE_TOTAL_ROYALTY_CHARGE: 'chargeRoyaltyTotal',

  // Carrier Numeric Codes (UPS)
  '206': 'chargeUps',
  '235': 'chargeUps',
  '341': 'chargeUps',
  '351': 'chargeUps',
  '400': 'chargeUps',
  '405': 'chargeUps',
  '410': 'chargeUps',
  '426': 'chargeUps',
  '619': 'chargeUps',
};

export const WizardCostBreakdown = ({ selectedRate }: WizardCostBreakdownProps) => {
  const intl = useIntl();
  const [isExpanded, setIsExpanded] = useState(false);

  const resolveChargeLabel = (charge: LtlQuoteCharge): string => {
    const messageKey = CHARGE_CODE_LABEL[charge.code];
    if (messageKey) {
      return intl.formatMessage(wizardCostBreakdownMessages[messageKey]);
    }
    return charge.description || charge.code;
  };

  const charges = selectedRate?.charges ?? [];

  const totalDisplay = selectedRate === null ? '$-' : intl.formatNumber(selectedRate.totalCharges, { style: 'currency', currency: 'USD' });

  return (
    <div aria-label={intl.formatMessage(wizardCostBreakdownMessages.title)} className={styles.costBreakdownSection} role='region'>
      <div className={styles.headerRow}>
        <Text fw={600} size='sm'>
          {intl.formatMessage(wizardCostBreakdownMessages.title)}
        </Text>
        <UnstyledButton
          aria-expanded={isExpanded}
          aria-label={intl.formatMessage(wizardCostBreakdownMessages.title)}
          className={styles.toggleButton}
          onClick={() => setIsExpanded((prev) => !prev)}
        >
          <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
        </UnstyledButton>
      </div>

      <Collapse in={isExpanded}>
        <div className={styles.chargesList}>
          {charges.map((charge, index) => (
            <div className={styles.chargeRow} data-testid={`cost-breakdown-charge-${charge.code}`} key={`${charge.code}-${index}`}>
              <Text className={styles.chargeLabel} size='sm'>
                {resolveChargeLabel(charge)}
              </Text>
              <Text className={styles.chargePrice} size='sm'>
                {intl.formatNumber(charge.price, { style: 'currency', currency: 'USD' })}
              </Text>
            </div>
          ))}
        </div>
      </Collapse>

      <div className={styles.totalRow}>
        <Text fw={600} size='sm'>
          {intl.formatMessage(wizardCostBreakdownMessages.total)}
        </Text>
        <Text fw={600} size='sm'>
          {totalDisplay}
        </Text>
      </div>

      <Text className={styles.disclaimer} size='xs'>
        {intl.formatMessage(wizardCostBreakdownMessages.disclaimer)}{' '}
        <Anchor
          // TODO(link): real SpeedShip billing management url
          c='var(--adl-button-primary-hyperlink-text)'
          href='#'
          rel='noopener noreferrer'
          size='xs'
          target='_blank'
        >
          {intl.formatMessage(wizardCostBreakdownMessages.manageBilling)}
        </Anchor>
      </Text>
    </div>
  );
};
